/**
 * Shared Right Panel – Summary / Current Visit / Sensei.
 * Integrates existing: Historical Summary → Summary tab; Live Summary → Current Visit > Summary;
 * Live transcript → Current Visit > Live Feed; Intellisense → Sensei > Ask Sensei.
 * Design: right_panel_summary_tab, right_panel_current_visit_*, right_panel_sensei_*.
 */
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link,
} from '@mui/material';
import {
  Person,
  Edit,
  History,
  Summarize,
  Warning,
  Create as PencilIcon,
  AutoAwesome,
  ExpandMore as ExpandMoreIcon,
  Assignment as ClipboardIcon,
  Refresh as RefreshIcon,
  AttachFile,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useSession } from '../../contexts/SessionContext';
import { useFormData } from '../../contexts/FormDataContext';
import { usePatientSummaryContext } from '../../contexts/PatientSummaryContext';
import MentalHealthIndexCard from './MentalHealthIndexCard';
import BwiAggregateCard from './BwiAggregateCard';
import LiveDiarizedTranscript, { type TranscriptDisplayMode } from '../Transcript/LiveDiarizedTranscript';
import MedicalChatbot from '../UnstructuredNotes/MedicalChatbot';
import { RIGHT_PANEL_WIDTH, RIGHT_PANEL_WIDTH_XS } from './constants';
import { getApiBaseUrl } from '../../utils/websocket';
import { formatSummaryText, parseSummarySections } from '../../utils/formatters';
import { useMainTab, MAIN_TAB } from '../../contexts/MainTabContext';

type PatientLabReportRow = {
  report_id: string;
  type: string;
  date: string | null;
  created_at: string;
  file_type: string;
  original_filename?: string | null;
};

function labReportPdfLinkLabel(r: PatientLabReportRow) {
  const rawFromUpload = r.original_filename ? String(r.original_filename) : '';
  const rawBase = rawFromUpload
    ? // Strip only the last extension from the original upload filename.
      rawFromUpload.replace(/\.[^/.]+$/, '')
    : (r.type || 'Lab').trim() || 'Lab';
  const safe = rawBase.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_') || 'Lab';
  const d = r.date
    ? String(r.date).slice(0, 10)
    : r.created_at
      ? new Date(r.created_at).toISOString().slice(0, 10)
      : 'unknown';
  const ext = (r.file_type || 'pdf').toLowerCase();
  return `${safe}_Report_${d}.${ext}`;
}

/** API times are "HH:mm"; format as 12-hour to match Pre-Consultation (e.g. 7:13 AM). */
function formatVisitListTime(hhmm: string | null | undefined): string {
  if (hhmm == null || String(hhmm).trim() === '') return '';
  const m = String(hhmm).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return String(hhmm);
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h > 23 || min > 59) return String(hhmm);
  const d = new Date(2000, 0, 1, h, min, 0, 0);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/* Right panel – subtle vertical gradients (shade/texture), not flat color. Sync across all parent tabs. */
const RIGHT_PANEL_TAB_INACTIVE = '#5a6c7d';
const RIGHT_PANEL_TAB_ACTIVE = '#012676';
const RIGHT_PANEL_SUBHEADER_GREY = '#4a5568';

/** Main tab bar and sub-header bar: same gradient for consistency. */
/** Sub-header bar: vertical gradient top → down. Almost white at top, barely perceptible light blue at bottom. */
const SUB_HEADER_BAR_GRADIENT = 'linear-gradient(to bottom, #F9FCFF 0%, #F2F7FC 50%, #e6f3ff 100%)';

/** Content area below headers: pure white */
const CONTENT_AREA_BG = '#FFFFFF';

/** Active sub-tab pill background (lighter blue, per design) */
const SUB_TAB_ACTIVE_BG = '#E9F5FF';

interface RightPanelTabsProps {
  showCurrentVisitContent?: boolean;
  defaultTab?: 'summary' | 'currentVisit' | 'sensei';
  sessionId?: string | null;
}

export default function RightPanelTabs({
  showCurrentVisitContent = false,
  defaultTab = 'summary',
  sessionId = null,
}: RightPanelTabsProps) {
  const { patient } = useUser();
  const { session, beginFormSessionEdit, formSessionOverride } = useSession();
  const { getFieldValue } = useFormData();
  const { navigateToTabIndex, ensureWorkflowEntered } = useMainTab();
  const [mainTab, setMainTab] = useState<'summary' | 'currentVisit' | 'sensei'>(defaultTab);
  const [currentVisitSubTab, setCurrentVisitSubTab] = useState<'summary' | 'live'>('summary');
  const [senseiSubTab, setSenseiSubTab] = useState<'recommendations' | 'ask'>('recommendations');

  /** Tracks last historical-edit session id so we switch to Summary on enter/override change, without clobbering manual tab picks on every render. */
  const historicalEditSessionRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const sid = formSessionOverride?.session_id ?? null;
    const prev = historicalEditSessionRef.current;
    if (sid) {
      if (!prev || prev !== sid) {
        setMainTab('summary');
      }
    } else if (prev) {
      setMainTab(defaultTab);
    }
    historicalEditSessionRef.current = sid;
  }, [formSessionOverride?.session_id, defaultTab]);

  // Sync right panel view from workflow/mic actions.
  useEffect(() => {
    const onShowSummary = () => {
      setMainTab('summary');
      setCurrentVisitSubTab('summary');
    };
    const onShowLiveFeed = () => {
      setMainTab('currentVisit');
      setCurrentVisitSubTab('live');
    };
    window.addEventListener('right-panel:show-summary', onShowSummary as EventListener);
    window.addEventListener('right-panel:show-live-feed', onShowLiveFeed as EventListener);
    return () => {
      window.removeEventListener('right-panel:show-summary', onShowSummary as EventListener);
      window.removeEventListener('right-panel:show-live-feed', onShowLiveFeed as EventListener);
    };
  }, []);

  const patientId = patient ? String(patient.patient_id) : undefined;
  const registrationPending = !patient;
  const visitId = session?.visit_id ?? null;
  const currentPreconsultArrival = String(getFieldValue('PreConsultation', 'arrival_time', '') || '').trim();
  const isLiveSessionActive = session?.status === 'active';

  const [diarizedLines, setDiarizedLines] = useState<any[]>([]);
  const [bufferTranscription, setBufferTranscription] = useState('');
  const [bufferDiarization, setBufferDiarization] = useState('');
  const [bufferTranslation, setBufferTranslation] = useState('');
  const [remainingTimeTranscription, setRemainingTimeTranscription] = useState(0);
  const [remainingTimeDiarization, setRemainingTimeDiarization] = useState(0);
  const [transcriptionStatus, setTranscriptionStatus] = useState('idle');
  const [savedTranscripts, setSavedTranscripts] = useState<any[]>([]);

  const {
    historicalSummary,
    historicalLoading,
    historicalError,
    fetchHistoricalSummary,
    realtimeSummary,
    realtimeLoading,
    realtimeError,
    fetchRealtimeSummary,
    clinicalRecommendation,
    clinicalLoading,
    clinicalError,
    fetchClinicalRecommendation,
    generateClinicalRecommendation,
    setHasTranscripts,
  } = usePatientSummaryContext();
  // const [transcriptDisplayMode, setTranscriptDisplayMode] = useState<TranscriptDisplayMode>('both');  
  const [transcriptDisplayMode, setTranscriptDisplayMode] = useState<TranscriptDisplayMode>('source');

  useEffect(() => {
    if (!sessionId) {
      setDiarizedLines([]);
      setSavedTranscripts([]);
      setBufferTranscription('');
      setBufferDiarization('');
      setBufferTranslation('');
      setTranscriptionStatus('idle');
      return;
    }
    const loadSaved = async () => {
      try {
        const res = await fetch(`/api/transcripts/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setSavedTranscripts(data);
        }
      } catch (_) {}
    };
    loadSaved();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const pollInterval = setInterval(() => {
      const data = (window as any).__whisperLiveKitData;
      const dataSessionId = (window as any).__whisperLiveKitSessionId;
      const myId = String(sessionId).toLowerCase();
      const dataId = dataSessionId != null ? String(dataSessionId).toLowerCase() : null;
      const isForThisSession = dataId != null && myId === dataId;
      if (data && isForThisSession) {
        // Only update lines when non-empty: WLK sends lines:[] while diarization is still catching up
        // (transcription done but speaker assignment pending). Clearing on empty wipes the transcript display.
        if (data.lines && data.lines.length > 0) {
          setDiarizedLines(data.lines);
        }
        setBufferTranscription(data.buffer_transcription || '');
        setBufferDiarization(data.buffer_diarization || '');
        setBufferTranslation(data.buffer_translation || '');
        setRemainingTimeTranscription(data.remaining_time_transcription || 0);
        setRemainingTimeDiarization(data.remaining_time_diarization || 0);
        setTranscriptionStatus(data.status || 'active_transcription');
      } else if (dataId && !isForThisSession) {
        setDiarizedLines([]);
        setBufferTranscription('');
        setBufferDiarization('');
        setTranscriptionStatus('idle');
      }
    }, 100);
    return () => clearInterval(pollInterval);
  }, [sessionId]);

  // Notify the shared PatientSummaryContext when transcripts become available so it
  // can start the realtime summary auto-refresh. One notification is enough — the
  // context ignores repeated true calls since the interval is already running.
  useEffect(() => {
    if (diarizedLines.length > 0 || savedTranscripts.length > 0) {
      setHasTranscripts(true);
    }
  }, [diarizedLines.length, savedTranscripts.length, setHasTranscripts]);

  const combinedTranscriptLines = useMemo(() => {
    if (diarizedLines.length > 0) return diarizedLines;
    return savedTranscripts.map((t) => ({
      speaker: t.speaker_number,
      text: t.text,
      start: t.start,
      end: t.end,
      detected_language: t.language,
    }));
  }, [diarizedLines, savedTranscripts]);

  const [previousSessions, setPreviousSessions] = useState<any[]>([]);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [visitTranscripts, setVisitTranscripts] = useState<Record<string, any[]>>({});
  const [visitSummaries, setVisitSummaries] = useState<Record<string, { summary: string; key_points: string[] }>>({});
  const [visitLoading, setVisitLoading] = useState<Record<string, boolean>>({});
  const [historicalEditOpen, setHistoricalEditOpen] = useState(false);
  const [historicalEditSaving, setHistoricalEditSaving] = useState(false);
  const [patientLabReports, setPatientLabReports] = useState<PatientLabReportRow[]>([]);
  const [patientLabLoading, setPatientLabLoading] = useState(false);
  const [historicalEditForm, setHistoricalEditForm] = useState({
    clinical_summary: '',
    treatment_summary: '',
    comorbidity: [] as string[],
    allergies: [] as string[],
    lifestyle: [] as string[],
    medical_history: '',
  });

  const previousVisitRows = previousSessions;

  // Previous visits: try visits API (visit-wise), then sessions API; use base URL for correct origin
  const fetchPreviousVisits = useCallback(() => {
    if (!patientId) {
      setPreviousSessions([]);
      return () => {};
    }
    const base = getApiBaseUrl();
    const opts = { headers: { 'ngrok-skip-browser-warning': 'true' } as Record<string, string> };
    let cancelled = false;

    const setList = (list: any[]) => {
      if (!cancelled) {
        const raw = Array.isArray(list) ? list : [];
        const seen = new Set<string>();
        const deduped = raw.filter((item: any) => {
          const key = String(item.session_id || item.visit_id || item.id || item.created_at || '');
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setPreviousSessions(deduped);
      }
    };

    fetch(`${base}/api/patient-summary/${patientId}/visits`, opts)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data?.visits?.length) {
          setList(data.visits);
          return;
        }
        return fetch(`${base}/api/transcripts/patient/${patientId}/sessions`, opts).then((r) => (r.ok ? r.json() : []));
      })
      .then((sessionsOrUndefined) => {
        if (cancelled) return;
        if (Array.isArray(sessionsOrUndefined)) setList(sessionsOrUndefined);
      })
      .catch(() => {
        if (cancelled) return;
        fetch(`${base}/api/transcripts/patient/${patientId}/sessions`, opts)
          .then((res) => (res.ok ? res.json() : []))
          .then(setList)
          .catch(() => setList([]));
      });

    return () => { cancelled = true; };
  }, [patientId]);

  // Clear stale visits immediately when patient changes so old patient's data never flashes
  useEffect(() => {
    setPreviousSessions([]);
  }, [patientId]);

  useEffect(() => {
    const cancel = fetchPreviousVisits();
    return () => { cancel(); };
  // }, [patientId]);
  // Nikhil added start - 08-04-2026 - Issue #4: re-fetch previous visits when session ends or visit changes so doctor doesn't need manual page refresh
  }, [fetchPreviousVisits, visitId, session?.status]);
  // Nikhil added end

  // End-visit can be eventually consistent for a short window. Refresh previous visits
  // immediately + once after a short delay when Summary is requested.
  useEffect(() => {
    const onShowSummary = () => {
      const cancelNow = fetchPreviousVisits();
      const t = setTimeout(() => {
        const cancelLater = fetchPreviousVisits();
        // No-op cleanup for delayed call cancellation function.
        if (typeof cancelLater === 'function') {
          // keep reference only to avoid eslint "unused var"; function handles internal cancelled flag
          void cancelLater;
        }
      }, 1500);
      return () => {
        cancelNow();
        clearTimeout(t);
      };
    };
    const handler = () => { onShowSummary(); };
    window.addEventListener('right-panel:show-summary', handler as EventListener);
    window.addEventListener('previous-visits:refresh', handler as EventListener);
    return () => {
      window.removeEventListener('right-panel:show-summary', handler as EventListener);
      window.removeEventListener('previous-visits:refresh', handler as EventListener);
    };
  }, [fetchPreviousVisits]);

  useEffect(() => {
    if (!patientId) {
      setPatientLabReports([]);
      return;
    }
    const base = getApiBaseUrl();
    const opts = { headers: { 'ngrok-skip-browser-warning': 'true' } as Record<string, string> };
    setPatientLabLoading(true);
    fetch(`${base}/api/lab-reports/all?patient_id=${encodeURIComponent(patientId)}&limit=200`, opts)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data) => {
        const list = Array.isArray(data?.reports) ? data.reports : [];
        setPatientLabReports(list as PatientLabReportRow[]);
      })
      .catch(() => setPatientLabReports([]))
      .finally(() => setPatientLabLoading(false));
  }, [patientId]);

  const handleEditFormForVisit = useCallback(
    async (histSessionId: string) => {
      try {
        await beginFormSessionEdit(histSessionId);
        ensureWorkflowEntered();
        navigateToTabIndex(MAIN_TAB.registration);
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Could not open visit forms');
      }
    },
    [beginFormSessionEdit, ensureWorkflowEntered, navigateToTabIndex]
  );

  const loadVisitDetails = useCallback(async (sessionId: string) => {
    if (!sessionId) return;
    setVisitLoading((prev) => ({ ...prev, [sessionId]: true }));
    const base = getApiBaseUrl();
    const opts = { headers: { 'ngrok-skip-browser-warning': 'true' } as Record<string, string> };
    try {
      const [transRes, sumRes] = await Promise.all([
        fetch(`${base}/api/transcripts/${sessionId}`, opts),
        fetch(`${base}/api/patient-summary/${sessionId}/realtime`, { ...opts, method: 'POST' }),
      ]);
      const transcripts = transRes.ok ? await transRes.json() : [];
      let summary = '';
      let key_points: string[] = [];
      if (sumRes.ok) {
        const sumData = await sumRes.json();
        summary = sumData.summary || '';
        key_points = sumData.key_points || [];
      }
      setVisitTranscripts((prev) => ({ ...prev, [sessionId]: Array.isArray(transcripts) ? transcripts : [] }));
      setVisitSummaries((prev) => ({ ...prev, [sessionId]: { summary, key_points } }));
    } catch (_) {
      setVisitTranscripts((prev) => ({ ...prev, [sessionId]: [] }));
      setVisitSummaries((prev) => ({ ...prev, [sessionId]: { summary: '', key_points: [] } }));
    } finally {
      setVisitLoading((prev) => ({ ...prev, [sessionId]: false }));
    }
  }, []);

  useEffect(() => {
    if (mainTab === 'summary' && patientId && !historicalSummary && !historicalLoading) {
      fetchHistoricalSummary();
    }
  }, [mainTab, patientId, historicalSummary, historicalLoading, fetchHistoricalSummary]);

  useEffect(() => {
    if (historicalEditOpen && historicalSummary) {
      const h = historicalSummary as any;
      setHistoricalEditForm({
        clinical_summary: h.clinical_summary ?? h.summary ?? '',
        treatment_summary: h.treatment_summary ?? '',
        comorbidity: Array.isArray(h.comorbidity) ? [...h.comorbidity] : [],
        allergies: Array.isArray(h.allergies) ? [...h.allergies] : [],
        lifestyle: Array.isArray(h.lifestyle) ? [...h.lifestyle] : [],
        medical_history: h.medical_history ?? '',
      });
    }
  }, [historicalEditOpen, historicalSummary]);

  const saveHistoricalEdit = useCallback(async () => {
    if (!patientId) return;
    setHistoricalEditSaving(true);
    const apiBaseUrl = getApiBaseUrl();
    try {
      const res = await fetch(`${apiBaseUrl}/api/patient-summary/${patientId}/historical`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
        body: JSON.stringify({
          clinical_summary: historicalEditForm.clinical_summary || undefined,
          treatment_summary: historicalEditForm.treatment_summary || undefined,
          comorbidity: historicalEditForm.comorbidity.length ? historicalEditForm.comorbidity : undefined,
          allergies: historicalEditForm.allergies.length ? historicalEditForm.allergies : undefined,
          lifestyle: historicalEditForm.lifestyle.length ? historicalEditForm.lifestyle : undefined,
          medical_history: historicalEditForm.medical_history || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || res.statusText);
      }
      setHistoricalEditOpen(false);
      await fetchHistoricalSummary();
    } catch (e) {
      console.error('Save historical summary failed:', e);
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setHistoricalEditSaving(false);
    }
  }, [patientId, historicalEditForm, fetchHistoricalSummary]);

  useEffect(() => {
    if (mainTab === 'currentVisit' && currentVisitSubTab === 'summary' && sessionId && !realtimeSummary && !realtimeLoading) {
      fetchRealtimeSummary();
    }
  }, [mainTab, currentVisitSubTab, sessionId, realtimeSummary, realtimeLoading, fetchRealtimeSummary]);

  useEffect(() => {
    if (mainTab === 'sensei' && senseiSubTab === 'recommendations' && patientId && visitId != null) {
      fetchClinicalRecommendation(visitId);
    }
  }, [mainTab, senseiSubTab, patientId, visitId, fetchClinicalRecommendation]);

  const patientName = patient?.name || '–';
  const patientMrn = patient?.mrn || '–';

  const lastUpdatedStr =
    realtimeSummary?.generated_at
      ? new Date(realtimeSummary.generated_at).toLocaleString('en-GB', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : sessionId
        ? '–'
        : '–';

  const tabSx = {
    minHeight: 42,
    px: 2,
    background: SUB_HEADER_BAR_GRADIENT,
    backgroundAttachment: 'scroll',
    borderBottom: '1px solid #e8ecf2',
    '& .MuiTabs-scroller': { background: 'transparent' },
    '& .MuiTabs-flexContainer': { background: 'transparent' },
    '& .MuiTab-root': {
      minHeight: 42,
      textTransform: 'none',
      fontSize: '0.8rem',
      color: RIGHT_PANEL_TAB_INACTIVE,
      fontWeight: 500,
    },
    '& .Mui-selected': { fontWeight: 600, color: RIGHT_PANEL_TAB_ACTIVE },
    '& .MuiTabs-indicator': { bgcolor: RIGHT_PANEL_TAB_ACTIVE, height: 2 },
  };

  /* Sub-header bar: full width, sub-tabs equally split (50% each), labels center-aligned. */
  const subHeaderBarSx = {
    minHeight: 36,
    background: SUB_HEADER_BAR_GRADIENT,
    borderBottom: '1px solid #e8ecf2',
    display: 'flex',
    flexDirection: 'row' as const,
    width: '100%',
    alignItems: 'stretch',
  };

  const renderSubTab = (
    label: React.ReactNode,
    value: string,
    current: string,
    onChange: (v: string) => void,
    options?: { icon?: React.ReactNode }
  ) => {
    const isActive = current === value;
    return (
      <Box
        key={value}
        component="button"
        type="button"
        onClick={() => onChange(value)}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          minHeight: 36,
          minWidth: 0,
          border: 'none',
          background: isActive ? SUB_TAB_ACTIVE_BG : 'transparent',
          color: isActive ? RIGHT_PANEL_TAB_ACTIVE : RIGHT_PANEL_TAB_INACTIVE,
          fontWeight: isActive ? 600 : 500,
          fontSize: '0.75rem',
          textTransform: 'none',
          cursor: 'pointer',
          borderRadius: 0,
          borderBottom: isActive ? `2px solid ${RIGHT_PANEL_TAB_ACTIVE}` : '2px solid transparent',
          '&:hover': { background: isActive ? SUB_TAB_ACTIVE_BG : 'rgba(0,0,0,0.04)' },
        }}
      >
        {options?.icon}
        {label}
      </Box>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: RIGHT_PANEL_WIDTH_XS, md: RIGHT_PANEL_WIDTH },
        minWidth: { md: RIGHT_PANEL_WIDTH - 20 },
        flexShrink: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        // height: { xs: 'auto', md: '100%' },        
        height: '100%',
        borderColor: '#d0dae6',
      }}
    >
      <Tabs
        value={mainTab}
        onChange={(_, v) => {
          if (v === 'currentVisit' && registrationPending) return;
          setMainTab(v);
        }}
        variant="fullWidth"
        sx={tabSx}
      >
        <Tab label="Summary" value="summary" />
        <Tab
          label="Current Visit"
          value="currentVisit"
          disabled={registrationPending}
          title={registrationPending ? 'Complete registration first' : undefined}
          sx={registrationPending ? { opacity: 0.5 } : undefined}
        />
        <Tab label="Sensei" value="sensei" />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'scroll',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: CONTENT_AREA_BG,
        }}
      >
        {/* ---------- SUMMARY TAB (right_panel_summary_tab.png) – Historical Summary ---------- */}
        {mainTab === 'summary' && (
          // <Box sx={{ p: 2, pb: 3, bgcolor: CONTENT_AREA_BG, minHeight: '100%' }}>          
          <Box sx={{ p: 2, pb: 3, bgcolor: CONTENT_AREA_BG, minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Person sx={{ color: RIGHT_PANEL_TAB_ACTIVE, fontSize: 20 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Patient</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{patientName}</Typography>
                <Typography variant="caption" color="text.secondary">MRN: {patientMrn}</Typography>
              </Box>
            </Box>

            {/* Historical Summary – header with badge and refresh */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <History sx={{ fontSize: 20, color: RIGHT_PANEL_TAB_ACTIVE }} />
                Historical Summary
              </Typography>
              <Chip
                size="small"
                label={
                  historicalSummary
                    ? `${historicalSummary.visit_count} Previous Visits${
                        historicalSummary.visits_used_for_summary &&
                        historicalSummary.visits_used_for_summary < historicalSummary.visit_count
                          ? ` (last ${historicalSummary.visits_used_for_summary} summarized)`
                          : ''
                      }`
                    : '0 Previous Visits'
                }
                sx={{ bgcolor: '#E9F5FF', color: RIGHT_PANEL_TAB_ACTIVE, fontWeight: 500 }}
              />
              <IconButton size="small" onClick={() => fetchHistoricalSummary(true)} disabled={historicalLoading} title="Regenerate summary from all visits" sx={{ ml: 0.5 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            {historicalLoading && (
              <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">Generating...</Typography>
              </Box>
            )}
            {historicalError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {historicalError}
              </Alert>
            )}
            {historicalSummary && !historicalLoading && (
              <>
                {/* Clinical Summary */}
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mb: 1.5 }}>
                  <Person sx={{ color: RIGHT_PANEL_SUBHEADER_GREY, fontSize: 18, mt: 0.25 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Clinical Summary</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{formatSummaryText((historicalSummary as any).clinical_summary ?? historicalSummary.summary)}</Typography>
                  </Box>
                </Box>
                {/* Treatment Summary */}
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mb: 1.5 }}>
                  <ClipboardIcon sx={{ color: RIGHT_PANEL_SUBHEADER_GREY, fontSize: 18, mt: 0.25 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Treatment Summary</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{formatSummaryText((historicalSummary as any).treatment_summary) || 'From visit data when available.'}</Typography>
                  </Box>
                </Box>
                {/* Comorbidity / Allergies / Lifestyle cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 1.5 }}>
                  <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Comorbidity</Typography>
                      <Typography variant="body2" component="ul" sx={{ mt: 0.25, m: 0, pl: 2 }}>{(historicalSummary as any).comorbidity?.length ? (historicalSummary as any).comorbidity.map((c: string, i: number) => <li key={i}>{c}</li>) : '–'}</Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ bgcolor: '#fffbf0' }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Allergies</Typography>
                      <Typography variant="body2" component="ul" sx={{ mt: 0.25, m: 0, pl: 2 }}>{(historicalSummary as any).allergies?.length ? (historicalSummary as any).allergies.map((a: string, i: number) => <li key={i}>{a}</li>) : '–'}</Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ bgcolor: '#f0f9f0' }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Lifestyle</Typography>
                      <Typography variant="body2" component="ul" sx={{ mt: 0.25, m: 0, pl: 2 }}>{(historicalSummary as any).lifestyle?.length ? (historicalSummary as any).lifestyle.map((l: string, i: number) => <li key={i}>{l}</li>) : '–'}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                {/* Medical History */}
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mb: 1 }}>
                  <Person sx={{ color: RIGHT_PANEL_SUBHEADER_GREY, fontSize: 18, mt: 0.25 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Medical History</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{formatSummaryText((historicalSummary as any).medical_history) || '–'}</Typography>
                  </Box>
                </Box>
              </>
            )}
            {!historicalSummary && !historicalLoading && patientId && (
              <Typography variant="body2" color="text.secondary">No historical data. Start a visit to build summary.</Typography>
            )}
            {!patientId && (
              <Typography variant="body2" color="text.secondary">Select a patient to view summary.</Typography>
            )}

            {patientId && (
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: historicalSummary && !historicalLoading ? 0 : 2, mb: 0.5 }}>
                <AttachFile sx={{ color: RIGHT_PANEL_SUBHEADER_GREY, fontSize: 18, mt: 0.25 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Lab Results</Typography>
                  {patientLabLoading ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Loading reports…</Typography>
                  ) : patientLabReports.length === 0 ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>–</Typography>
                  ) : (
                    <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                      {patientLabReports.map((r) => (
                        <Box component="li" key={r.report_id} sx={{ mb: 0.25 }}>
                          <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={() => {
                              const base = getApiBaseUrl();
                              window.open(`${base}/api/lab-reports/${r.report_id}/pdf`, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ cursor: 'pointer', textAlign: 'left', p: 0, verticalAlign: 'baseline' }}
                          >
                            {labReportPdfLinkLabel(r)}
                          </Link>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {historicalSummary && !historicalLoading && (
              <Button size="small" startIcon={<Edit />} sx={{ mt: 1, color: '#012676' }} onClick={() => setHistoricalEditOpen(true)}>Edit</Button>
            )}

            {/* Behaviour Wellness & Impact — aggregated across all visits */}
            {patientId && (
              <Box sx={{ mt: 2 }}>
                <BwiAggregateCard patientId={patient?.patient_id ?? null} />
              </Box>
            )}

            {/* Previous Visits – visit-wise, expand for summary + transcript */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>Previous Visits</Typography>
            <List dense disablePadding sx={{ bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
              {previousVisitRows.length === 0 ? (
                <ListItem><ListItemText primary="No previous visits" secondary="Sessions and transcripts will appear here after visits." /></ListItem>
              ) : (
                previousVisitRows.map((item: any) => {
                  const sid = item.session_id || item.id;
                  const rowKey = sid || `v-${item.visit_id || item.created_at}`;
                  const label = item.visit_type || 'Visit';
                  const isActiveVisitRow = session?.status === 'active' && (
                    (visitId != null && item.visit_id != null && Number(item.visit_id) === Number(visitId)) ||
                    (sessionId && sid && String(sessionId) === String(sid))
                  );
                  const effectiveArrival =
                    isActiveVisitRow && currentPreconsultArrival
                      ? currentPreconsultArrival
                      : item.arrival_time;
                  const timeForRow = effectiveArrival || item.visit_time;
                  const timeStr = timeForRow
                    ? formatVisitListTime(timeForRow)
                    : item.created_at
                      ? new Date(item.created_at).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                      : '';
                  const dateStr = item.visit_date
                    ? new Date(item.visit_date + (timeForRow ? `T${timeForRow}` : '')).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })
                    : item.created_at
                      ? new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
                      : '';
                  const primaryText = `${label}: ${timeStr}${timeStr && dateStr ? ' on ' : ''}${dateStr}`;
                  const isExpanded = expandedSessionId === rowKey;
                  const hasSession = Boolean(sid);
                  return (
                    <Box key={rowKey}>
                      <ListItem
                        button
                        onClick={() => {
                          setExpandedSessionId(isExpanded ? null : rowKey);
                          if (!isExpanded && hasSession) loadVisitDetails(sid);
                        }}
                        sx={{ borderBottom: '1px solid #eee' }}
                      >
                        <ListItemText
                          primary={primaryText}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                        <ExpandMoreIcon
                          sx={{
                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItem>

                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 2, pr: 2, pb: 2, pt: 0 }}>
                          {/* ── Action buttons row: Edit Form + Refresh ── */}
                          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {/* Edit Form — only when visit has a session */}
                            {hasSession && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={isLiveSessionActive}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  color: '#012676',
                                  borderColor: '#90caf9',
                                  bgcolor: '#f5fbff',
                                  opacity: isLiveSessionActive ? 0.6 : 1,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditFormForVisit(sid);
                                }}
                              >
                                Edit Form
                              </Button>
                            )}

                            {/* Refresh – present on every visit */}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                visitLoading[rowKey]
                                  ? <CircularProgress size={12} sx={{ color: '#012676' }} />
                                  : <RefreshIcon fontSize="small" />
                              }
                              disabled={visitLoading[rowKey] || !sid}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                color: '#012676',
                                borderColor: '#90caf9',
                                bgcolor: '#f5fbff',
                                opacity: !sid ? 0.5 : 1,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (sid) loadVisitDetails(sid);
                              }}
                            >
                              {visitLoading[rowKey] ? 'Refreshing...' : 'Refresh'}
                            </Button>
                          </Box>

                          {!hasSession && (
                            <Typography variant="body2" color="text.secondary">No transcript or summary for this visit.</Typography>
                          )}

                          {hasSession && visitLoading[rowKey] && (
                            <Box display="flex" alignItems="center" gap={1} py={1}>
                              <CircularProgress size={16} />
                              <Typography variant="caption" color="text.secondary">Loading transcript and summary…</Typography>
                            </Box>
                          )}

                          {hasSession && !visitLoading[rowKey] && (
                            <>
                              {/* Visit Summary label (no duplicate Refresh here) */}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                              >
                                Visit summary
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}
                              >
                                {formatSummaryText(visitSummaries[sid]?.summary) || '–'}
                              </Typography>

                              {(visitSummaries[sid]?.key_points?.length ?? 0) > 0 && (
                                <Box component="ul" sx={{ m: 0, pl: 2, mb: 1.5 }}>
                                  {visitSummaries[sid].key_points.map((kp: string, i: number) => (
                                    <li key={i}><Typography variant="body2" component="span">{formatSummaryText(kp)}</Typography></li>
                                  ))}
                                </Box>
                              )}

                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Transcript</Typography>
                              {(visitTranscripts[sid] || []).length > 0 ? (
                                <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1, mt: 0.5, bgcolor: '#fff' }}>
                                  {(visitTranscripts[sid] || []).map((t: any, i: number) => (
                                    <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                                      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{t.speaker || `Speaker ${t.speaker_number}`}:</Typography>
                                      {' '}{t.text}
                                    </Typography>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No transcript.</Typography>
                              )}

                              {/* Behaviour Wellness & Impact Index for this historical visit */}
                              {item.visit_id != null && (
                                <Box sx={{ mt: 1.5 }}>
                                  <MentalHealthIndexCard visitId={item.visit_id} sessionId={sid} />
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })
              )}
            </List>

            {/* Edit Historical Summary Dialog */}
            <Dialog open={historicalEditOpen} onClose={() => setHistoricalEditOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Edit Historical Summary</DialogTitle>
              <DialogContent>
                <TextField fullWidth label="Clinical Summary" multiline rows={3} value={historicalEditForm.clinical_summary} onChange={(e) => setHistoricalEditForm((f) => ({ ...f, clinical_summary: e.target.value }))} sx={{ mt: 1 }} />
                <TextField fullWidth label="Treatment Summary" multiline rows={2} value={historicalEditForm.treatment_summary} onChange={(e) => setHistoricalEditForm((f) => ({ ...f, treatment_summary: e.target.value }))} sx={{ mt: 1.5 }} />
                <TextField fullWidth label="Comorbidity (one per line)" multiline rows={2} value={historicalEditForm.comorbidity.join('\n')} onChange={(e) => setHistoricalEditForm((f) => ({ ...f, comorbidity: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) }))} sx={{ mt: 1.5 }} />
                <TextField fullWidth label="Allergies (one per line)" multiline rows={2} value={historicalEditForm.allergies.join('\n')} onChange={(e) => setHistoricalEditForm((f) => ({ ...f, allergies: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) }))} sx={{ mt: 1.5 }} />
                <TextField fullWidth label="Lifestyle (one per line)" multiline rows={2} value={historicalEditForm.lifestyle.join('\n')} onChange={(e) => setHistoricalEditForm((f) => ({ ...f, lifestyle: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) }))} sx={{ mt: 1.5 }} />
                <TextField fullWidth label="Medical History" multiline rows={3} value={historicalEditForm.medical_history} onChange={(e) => setHistoricalEditForm((f) => ({ ...f, medical_history: e.target.value }))} sx={{ mt: 1.5 }} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setHistoricalEditOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={saveHistoricalEdit} disabled={historicalEditSaving} sx={{ bgcolor: '#012676' }}>{historicalEditSaving ? 'Saving...' : 'Save'}</Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* ---------- CURRENT VISIT (right_panel_current_visit_summary_tab / live_tab) ---------- */}
        {mainTab === 'currentVisit' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Typography variant="caption" sx={{ px: 2, pt: 1.5, color: RIGHT_PANEL_SUBHEADER_GREY }}>
              Last Updated: {lastUpdatedStr}
            </Typography>
            <Box sx={subHeaderBarSx}>
              {renderSubTab('Current Visit Summary', 'summary', currentVisitSubTab, (v) => setCurrentVisitSubTab(v as 'summary' | 'live'))}
              {renderSubTab('Live Feed', 'live', currentVisitSubTab, (v) => setCurrentVisitSubTab(v as 'summary' | 'live'))}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, bgcolor: CONTENT_AREA_BG }}>
              {currentVisitSubTab === 'summary' && (
                <>
                  {realtimeLoading && !realtimeSummary?.summary && (
                    <Box display="flex" alignItems="center" gap={1} py={1}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">Generating...</Typography>
                    </Box>
                  )}
                  {!realtimeLoading && !realtimeSummary?.summary && (
                    <Typography variant="body2" color="text.secondary">No conversation data yet. Summary will appear as the conversation progresses.</Typography>
                  )}
                  {realtimeSummary?.summary && (() => {
                    const sections = parseSummarySections(realtimeSummary.summary);
                    return (
                      <Box>
                        {sections.map((sec, i) => (
                          <Box key={i} sx={{ mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{sec.title}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{sec.content || '–'}</Typography>
                          </Box>
                        ))}
                        {realtimeSummary?.key_points?.length ? (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Key points</Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                              {realtimeSummary.key_points.map((kp: string, idx: number) => (
                                <Typography key={idx} component="li" variant="body2" color="text.secondary">{formatSummaryText(kp)}</Typography>
                              ))}
                            </Box>
                          </Box>
                        ) : null}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1.5, gap: 0.5 }}>
                          <PencilIcon sx={{ fontSize: 16, color: '#012676' }} />
                          <Typography variant="caption" sx={{ color: '#012676' }}>{realtimeLoading ? 'Generating...' : ''}</Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                  {/* Behaviour Wellness & Impact Index — shown when a visit exists */}
                  {visitId != null && (
                    <Box sx={{ mt: 2 }}>
                      <MentalHealthIndexCard visitId={visitId} sessionId={sessionId} />
                    </Box>
                  )}
                </>
              )}
              {currentVisitSubTab === 'live' && (
                <Box sx={{ minHeight: 200 }}>
                  {sessionId && (session?.status === 'active' || combinedTranscriptLines.length > 0) ? (
                    <>
                      <Box
                        sx={{
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                          visibility: 'hidden',
                          height: 0,
                          overflow: 'hidden',
                          margin: 0,
                          padding: 0,
                        }}
                        aria-hidden
                      >
                        <Typography variant="caption" color="text.secondary">Display:</Typography>
                        <ToggleButtonGroup
                          size="small"
                          value={transcriptDisplayMode}
                          exclusive
                          onChange={(_, v) => v != null && setTranscriptDisplayMode(v)}
                          aria-label="What to show in transcript"
                        >
                          <ToggleButton value="source" aria-label="Source only">Source only</ToggleButton>
                          <ToggleButton value="translation" aria-label="Translation only">Translation only</ToggleButton>
                          <ToggleButton value="both" aria-label="Both">Both</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          (display only, applies to live feed)
                        </Typography>
                      </Box>
                      <LiveDiarizedTranscript
                        lines={combinedTranscriptLines}
                        bufferTranscription={bufferTranscription}
                        bufferDiarization={bufferDiarization}
                        bufferTranslation={bufferTranslation}
                        remainingTimeTranscription={remainingTimeTranscription}
                        remainingTimeDiarization={remainingTimeDiarization}
                        status={transcriptionStatus}
                        isFinalizing={false}
                        alternateSpeakersLeftRight
                        transcriptDisplayMode={transcriptDisplayMode}
                      />
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Start a session to see live conversation.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* ---------- SENSEI (right_panel_sensei_reccomendation_tab / ask_tab) ---------- */}
        {mainTab === 'sensei' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={subHeaderBarSx}>
              {renderSubTab(
                'AI Clinical Recommendations',
                'recommendations',
                senseiSubTab,
                (v) => setSenseiSubTab(v as 'recommendations' | 'ask')
              )}
              {renderSubTab(
                'Ask Sensei',
                'ask',
                senseiSubTab,
                (v) => setSenseiSubTab(v as 'recommendations' | 'ask'),
                { icon: <AutoAwesome sx={{ fontSize: 16, color: 'inherit' }} /> }
              )}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, bgcolor: CONTENT_AREA_BG }}>
              {senseiSubTab === 'recommendations' && (
                <>
                  {!patientId && (
                    <Typography variant="body2" color="text.secondary">
                      Select a patient from the dashboard to see AI clinical recommendations.
                    </Typography>
                  )}
                  {patientId && !visitId && (
                    <Typography variant="body2" color="text.secondary">
                      Start a visit (create a session) to see AI clinical recommendations for this patient.
                    </Typography>
                  )}
                  {patientId && visitId && clinicalLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">Loading recommendations…</Typography>
                    </Box>
                  )}
                  {patientId && visitId && clinicalError && (
                    <Alert severity="error" sx={{ mt: 1 }}>{clinicalError}</Alert>
                  )}
                  {patientId && visitId && !clinicalLoading && !clinicalRecommendation && !clinicalError && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No recommendations generated yet. Generate based on historical and current visit data.
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => generateClinicalRecommendation(visitId, sessionId || undefined)}
                        startIcon={<AutoAwesome />}
                      >
                        Generate recommendations
                      </Button>
                    </Box>
                  )}
                  {patientId && visitId && !clinicalLoading && clinicalRecommendation?.payload && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: RIGHT_PANEL_SUBHEADER_GREY, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Summarize sx={{ fontSize: 18, color: RIGHT_PANEL_SUBHEADER_GREY }} />
                          Recommended Care Pathway
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => generateClinicalRecommendation(visitId, sessionId || undefined)}
                          disabled={clinicalLoading}
                        >
                          Refresh
                        </Button>
                      </Box>
                      <Chip
                        label={clinicalRecommendation.payload.recommended_care_pathway || '–'}
                        size="small"
                        sx={{ mt: 1, bgcolor: '#e3f2fd', color: '#012676' }}
                      />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>Key Next Steps</Typography>
                      <List dense sx={{ listStyle: 'decimal', pl: 2.5 }}>
                        {(clinicalRecommendation.payload.key_next_steps || []).length > 0
                          ? (clinicalRecommendation.payload.key_next_steps || []).map((step, i) => (
                              <ListItem key={i} sx={{ display: 'list-item', py: 0 }}>
                                <ListItemText primary={step} />
                              </ListItem>
                            ))
                          : <ListItem sx={{ display: 'list-item', py: 0 }}><ListItemText primary="–" /></ListItem>
                        }
                      </List>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1.5 }}>Essential Questions to Ask</Typography>
                      <List dense sx={{ listStyle: 'disc', pl: 2.5 }}>
                        {(clinicalRecommendation.payload.essential_questions || []).length > 0
                          ? (clinicalRecommendation.payload.essential_questions || []).map((q, i) => (
                              <ListItem key={i} sx={{ display: 'list-item', py: 0 }}>
                                <ListItemText primary={q} />
                              </ListItem>
                            ))
                          : <ListItem sx={{ display: 'list-item', py: 0 }}><ListItemText primary="–" /></ListItem>
                        }
                      </List>
                      <Alert severity="warning" icon={<Warning />} sx={{ mt: 2 }} title="Clinical Alerts & Risk Factors">
                        {clinicalRecommendation.payload.clinical_alerts || 'No specific alerts at this time.'}
                      </Alert>
                    </>
                  )}
                </>
              )}
              {senseiSubTab === 'ask' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 300 }}>
                  {patientId ? (
                    <MedicalChatbot patientId={patientId} sessionId={sessionId || undefined} />
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select a patient to ask Sensei about their care.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {['Summarize this visit', 'Key risks?', 'Suggested labs'].map((q) => (
                          <Chip key={q} label={q} size="small" variant="outlined" sx={{ cursor: 'pointer' }} />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
